import { useEffect } from "react";
import ServicesNavbar from "@/pages/services/components/ServicesNavbar";
import ServicesHero from "@/pages/services/components/ServicesHero";
import ServicePanel, {
  type ServicePanelData,
} from "@/pages/services/components/ServicePanel";
import FirstSessionFlow from "@/pages/services/components/FirstSessionFlow";
import PricingTransparency from "@/pages/services/components/PricingTransparency";
import ServicesCTA from "@/pages/services/components/ServicesCTA";

/**
 * /services — the "Three ways to begin" page.
 *
 * Structure (top → bottom):
 *   1. Hero              — "Three ways to begin." + subtitle
 *   2. Path 01           — Individual Therapy
 *   3. Path 02           — Couples Therapy
 *   4. Path 03           — Online Sessions
 *   5. First session flow — 1 · 2 · 3 minimal timeline
 *   6. Pricing            — transparent per-row fee list
 *   7. Closing CTA        — Book Now / Have Questions First
 */

const SERVICE_PATHS: ServicePanelData[] = [
  {
    index: "01",
    title: "Individual Therapy",
    kicker: "One person, one quiet room.",
    guide:
      "For you, if lately you have been the one carrying everything, and the weight has started to feel like it belongs to you.",
    paragraph:
      "A 50-minute weekly space that is entirely yours. We work at your pace — sometimes with words, sometimes with silence. Common threads: burnout, anxiety, grief, relationships that echo old ones, and the quiet exhaustion of being okay for everyone else.",
    image:
      "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/62c760080221779d084a0ca3813ab0e5.png",
    bookHref: "/contacts",
  },
  {
    index: "02",
    title: "Couples Therapy",
    kicker: "Two voices, learning to listen.",
    guide:
      "For the two of you, if the same argument keeps returning in different clothes, and you both suspect the words are not the real problem.",
    paragraph:
      "An 80-minute session with both partners in the room. We do not take sides — we build a third space where the pattern between you becomes visible, and where each of you can be heard without having to raise your voice.",
    image:
      "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/920b156d9ce0f4fffe7a594d52853923.png",
    bookHref: "/contacts",
  },
  {
    index: "03",
    title: "Online Sessions",
    kicker: "Wherever feels safest to you.",
    guide:
      "For you, if getting to a room in the city is the reason you keep postponing, or if home is simply where you feel most yourself.",
    paragraph:
      "The same care, delivered through a private video link. Sessions run 50 minutes; you choose the room, the light, the tea. We work with clients across time zones — evenings and early mornings are available on request.",
    image:
      "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/5cf4c265d53a873b25b1ce7222cd37ed.png",
    bookHref: "/contacts",
  },
];

export default function Services() {
  // When arriving from /#services or with hash focus, scroll to top so the
  // page starts at its own hero instead of the middle of the second section.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <div className="relative w-full bg-background-50">
      <ServicesNavbar />
      <ServicesHero />

      <ServicePanel data={SERVICE_PATHS[0]} />
      <ServicePanel data={SERVICE_PATHS[1]} reverse />
      <ServicePanel data={SERVICE_PATHS[2]} />

      <FirstSessionFlow />
      <PricingTransparency />
      <ServicesCTA />
    </div>
  );
}